import { createAsyncThunk } from "@reduxjs/toolkit"
import apiRequest from "../../lib/http"
import { extractApiError } from "../../utils/errorHelpers"
import { linkTokenHeaders, linkTokenRefusalOf } from "../../utils/linkToken"

export const deletePayment = createAsyncThunk('payments/removePayment', async (paymentId, { rejectWithValue }) => {
  try {
    const response = await apiRequest.delete(`/payments/${paymentId}`)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to delete payment'
    return rejectWithValue(errorMessage)
  }
})
export const resetPlan = createAsyncThunk('plans/resetPlan', async ({ planId, userId }, { rejectWithValue }) => {
  try {
    const response = await apiRequest.post(`/plans/reset/${planId}/users/${userId}`)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to reset plan'
    return rejectWithValue(errorMessage)
  }
})

export const getPayments = createAsyncThunk('payments/getPayments', async (userId, { rejectWithValue }) => {
  try {
    const response = await apiRequest.get(`/payments/users/${userId}`)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to get payments'
    return rejectWithValue(errorMessage)
  }
})

export const getPaymentsReceived = createAsyncThunk('payments/getPaymentsReceived', async (userId, { rejectWithValue }) => {
  try {
    const response = await apiRequest.get(`/transactions/users/${userId}`)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to get transactions'
    return rejectWithValue(errorMessage)
  }
})

export const updateTransactionData = createAsyncThunk(
  'payments/updateTransactionData',
  async ({ transactionId, data }, { rejectWithValue }) => {
    try {
      const response = await apiRequest.put(`/transactions/${transactionId}`, data)
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to update transaction data'
      return rejectWithValue(errorMessage)
    }
  }
)

export const getPaymentById = createAsyncThunk(
  'payments/getPaymentById',
  async (paymentId, { rejectWithValue }) => {
    try {
      const response = await apiRequest.get(`/payments/${paymentId}`)
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to get payment data'
      return rejectWithValue(errorMessage)
    }
  }
)

export const getDashboardBalance = createAsyncThunk('payments/getDashboardBalance', async (userId, { rejectWithValue }) => {
  try {
    const response = await apiRequest.get(`/payments/dashboard/users/${userId}`)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to get dashboard balance'
    return rejectWithValue(errorMessage)
  }
})

export const getPaymentTypes = createAsyncThunk('payments/getPaymentTypes', async (_, { rejectWithValue }) => {
  try {
    const response = await apiRequest.get(`/payments/types`)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to get payment types'
    return rejectWithValue(errorMessage)
  }
})

export const getPaymentStatuses = createAsyncThunk('payments/getPaymentStatuses', async (_, { rejectWithValue }) => {
  try {
    const response = await apiRequest.get(`/payments/info/statuses`)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to get payment statuses'
    return rejectWithValue(errorMessage)
  }
})

export const getReturnReasons = createAsyncThunk('payments/getReturnReasons', async (_, { rejectWithValue }) => {
  try {
    const response = await apiRequest.get(`/payments/return-reasons`)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to get return reasons'
    return rejectWithValue(errorMessage)
  }
})

export const getBankAccountsDropdown = createAsyncThunk('payments/getBankAccountsDropdown', async (userId, { rejectWithValue }) => {
  try {
    const response = await apiRequest.get(`/bankaccounts/users/${userId}/dropdown`)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to get bank accounts'
    return rejectWithValue(errorMessage)
  }
})
export const createPlan = createAsyncThunk('payments/createPlan', async ({ userId, body }, { rejectWithValue }) => {
  try {
    const response = await apiRequest.post(`/plans/users/${userId}`, body)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to create plan'
    return rejectWithValue(errorMessage)
  }
})

// WS 2074858. Change the client's plan in place, from the Generate Plan form. A refusal comes back
// as the server's reason.
export const changePlan = createAsyncThunk('payments/changePlan', async ({ userId, body }, { rejectWithValue }) => {
  try {
    const response = await apiRequest.put(`/plans/users/${userId}`, body)
    return response.data
  } catch (error) {
    return rejectWithValue(error.response?.data?.error?.errors?.[0]?.message || 'Failed to change the plan')
  }
})

export const createSinglePayment = createAsyncThunk('payments/createPlan', async ({
                                                                                    userId,
                                                                                    body
                                                                                  }, { rejectWithValue }) => {
  try {
    const response = await apiRequest.post(`/payments/single/users/${userId}`, body)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to create plan'
    return rejectWithValue(errorMessage)
  }
})

export const getBankAccountsTypes = createAsyncThunk('payments/getBankAccountsTypes', async (userId, { rejectWithValue }) => {
  try {
    const response = await apiRequest.get(`/bankaccounts/users/${userId}/dropdown`)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to get bank account types'
    return rejectWithValue(errorMessage)
  }
})

export const updPayment = createAsyncThunk('payments/getBankAccountsTypes', async (data, { rejectWithValue }) => {
  try {
    const paymentId = data.pId
    const updData = data.formData
    const response = await apiRequest.put(`/payments/${paymentId}`, { ...updData })
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to update payment'
    return rejectWithValue(errorMessage)
  }
})

// Accepts a bare paymentId (legacy) or { paymentId, gateway, processor } — gateway/processor
// are a PER-CHARGE override from the Process dialog's picker (WS 1241875); they never change
// company/account defaults. Absent -> the backend routes by its shared default rule.
export const changePaymentsProcess = createAsyncThunk('payments/changePaymentsProcess', async (arg, { rejectWithValue }) => {
  try {
    const { paymentId, gateway, processor } = typeof arg === 'object' && arg !== null ? arg : { paymentId: arg }
    const body = gateway ? { gateway, ...(processor ? { processor } : {}) } : {}
    const response = await apiRequest.post(`/payments/${paymentId}/process`, body)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to process payment'
    return rejectWithValue(errorMessage)
  }
})

// WS 2074858. Reschedule a declined payment: the next attempt of the same payment on a new date.
// A refusal comes back as the server's reason, for the snackbar.
export const reschedulePaymentFetch = createAsyncThunk('payments/reschedulePayment', async ({ paymentId, dueDate }, { rejectWithValue }) => {
  try {
    const response = await apiRequest.post(`/payments/${paymentId}/reschedule`, { dueDate })
    return response.data
  } catch (error) {
    return rejectWithValue(error.response?.data?.error?.errors?.[0]?.message || 'Failed to reschedule the payment')
  }
})

// Options + suggestion for the Process dialog's gateway picker — same shared rule as the
// Daily Payment Processing rows (GET /payments/:id/gateway-options).
export const getPaymentGatewayOptions = createAsyncThunk('payments/getPaymentGatewayOptions', async (paymentId, { rejectWithValue }) => {
  try {
    const response = await apiRequest.get(`/payments/${paymentId}/gateway-options`)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to load gateway options'
    return rejectWithValue(errorMessage)
  }
})

// Optional fields beyond {paymentId, value} come from the Mark processed/failed modal:
// processedAt (the date the payment actually cleared/failed — suggested from its due
// date), a gateway transactionId, and failure reason fields. The backend ignores them
// for non-terminal statuses, so plain status changes keep sending an empty body.
export const changePaymentStatusFetch = createAsyncThunk('payments/changePaymentStatus', async ({
                                                                                                  paymentId,
                                                                                                  value,
                                                                                                  processedAt,
                                                                                                  transactionId,
                                                                                                  reasonText,
                                                                                                  reasonCode,
                                                                                                }, { rejectWithValue }) => {
  try {
    const body = {}
    if (processedAt) body.processedAt = processedAt
    if (transactionId) body.transactionId = transactionId
    if (reasonText) body.reasonText = reasonText
    if (reasonCode) body.reasonCode = reasonCode
    const response = await apiRequest.put(`/payments/${paymentId}/${value}`, body)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to change payment status'
    return rejectWithValue(errorMessage)
  }
})

// Void an in-flight charge on ANY gateway the backend says supports it. The gateway is resolved
// SERVER-SIDE from the payment's own transactionData.gateway — this thunk never names one, which
// is why the URL is no longer '/green/cancel'. The legacy per-gateway routes still exist
// server-side (they are the rollback path); this is the one the UI uses.
export const voidPaymentCharge = createAsyncThunk('payments/voidCharge', async ({ paymentId, reference }, { rejectWithValue }) => {
  try {
    const response = await apiRequest.post(`/payments/${paymentId}/void`, reference ? { reference } : {})
    return response.data
  } catch (error) {
    return rejectWithValue(extractApiError(error, 'Failed to void charge'))
  }
})

// Deprecated alias kept so any caller that still imports the old name keeps working. New code
// should import voidPaymentCharge.
export const cancelPaymentCharge = voidPaymentCharge

// Refund a processed charge on ANY gateway the backend says supports it. `refundAmount` may be
// partial when row.reversal.partialRefund is true; the server re-validates against the gateway's
// own capability and its own captured-amount cap, so a wrong amount is a clean 400 and never a
// wrong refund.
export const refundPaymentCharge = createAsyncThunk('payments/refundCharge', async ({ paymentId, refundAmount, refundMemo }, { rejectWithValue }) => {
  try {
    const response = await apiRequest.post(`/payments/${paymentId}/refund`, { refundAmount, refundMemo: refundMemo || '' })
    return response.data
  } catch (error) {
    return rejectWithValue(extractApiError(error, 'Failed to refund charge'))
  }
})

export const addCardPayment = createAsyncThunk('payment/addPayment', async (data, { rejectWithValue }) => {
  try {
    const response = await apiRequest.post(`/cards`, data)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to add card'
    return rejectWithValue(errorMessage)
  }
})

export const addBankPayment = createAsyncThunk('payment/addBankPayment', async (data, { rejectWithValue }) => {
  try {
    const response = await apiRequest.post(`/bankaccounts`, data)
    return response.data
  } catch (error) {
    return rejectWithValue(extractApiError(error, 'Failed to add bank account'))
  }
})

// WS 1406230 — the destructive endpoint. `removeOld` here runs a HARD BillingAccount.deleteMany
// for the contact, and until vlg-server!1221 this route was unauthenticated AND took the target
// `userId` from the request BODY, so anyone who had ever been sent an onboarding link could wipe
// any contact's billing accounts.
//
// With a scoped link token we post to the token-authenticated variant and send NO userId at all.
// The backend resolves the contact from the token and REFUSES a body userId that disagrees with
// it (403) rather than quietly preferring the token — so forwarding the userId out of the address
// bar would turn a tampered URL into a failed onboarding instead of a caught attack. Not sending
// one is also the stronger property: there is no input left to point at somebody else.
//
// Without a token the request is byte-for-byte what it is today. That is the grace path for the
// links already in clients' inboxes, and it is safe because the backend now ignores `removeOld`
// for unauthenticated, untokened callers.
export const addBankPaymentByLink = createAsyncThunk('payment/addBankPayment', async (data, { rejectWithValue }) => {
  try {
    const { linkToken, ...payload } = data || {}

    if (linkToken) {
      const tokenScopedPayload = { ...payload }
      delete tokenScopedPayload.userId
      const response = await apiRequest.post(`/bankaccounts-link/${linkToken}/anonymous`, tokenScopedPayload)
      return response.data
    }

    const response = await apiRequest.post(`/bankaccounts-link`, payload)
    return response.data
  } catch (error) {
    return rejectWithValue(extractApiError(error, 'Failed to add bank account by link'))
  }
})

export const addCreditCardByLink = createAsyncThunk('payment/addCreditCardByLink', async (data, { rejectWithValue }) => {
  try {
    const response = await apiRequest.post(`/cards-link`, data)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to add credit card by link'
    return rejectWithValue(errorMessage)
  }
})

export const getCardPayment = createAsyncThunk('payment/getCardPayment', async (userId, { rejectWithValue }) => {
  try {
    const response = await apiRequest.get(`/cards/users/${userId}`)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to get card payments'
    return rejectWithValue(errorMessage)
  }
})

export const getBankPayment = createAsyncThunk('payment/getBankPayment', async (userId, { rejectWithValue }) => {
  try {
    const response = await apiRequest.get(`/bankaccounts/users/${userId}`)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to get bank payments'
    return rejectWithValue(errorMessage)
  }
})

// The affiliate billing method — a client the affiliate collects from directly.
//
// Its own request rather than part of getBankPayment: /bankaccounts queries the BankAccount
// discriminator on the server and structurally cannot return one of these. A client who has none
// answers null, which is an answer and not an error — most clients have none.
export const getAffiliateBilling = createAsyncThunk('payment/getAffiliateBilling', async (userId, { rejectWithValue }) => {
  try {
    const response = await apiRequest.get(`/billing-accounts/affiliate/users/${userId}`)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to get affiliate billing'
    return rejectWithValue(errorMessage)
  }
})

// Give the client the affiliate billing method, so enrolment can finish with no banking details.
// The server re-checks the office permission and refuses if the office collects its own money.
export const addAffiliateBilling = createAsyncThunk('payment/addAffiliateBilling', async (userId, { rejectWithValue }) => {
  try {
    const response = await apiRequest.post(`/billing-accounts/affiliate/users/${userId}`)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to add affiliate billing'
    return rejectWithValue(errorMessage)
  }
})

export const getCreditCard = createAsyncThunk('payment/getBankPayment', async (userId, { rejectWithValue }) => {
  try {
    const response = await apiRequest.get(`/cards/users/${userId}`)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to get credit cards'
    return rejectWithValue(errorMessage)
  }
})

export const getBankAccountAnonymous = createAsyncThunk('bankAccount/getBankAccountAnonymous', async ({ userId }, { rejectWithValue }) => {
  try {
    const response = await apiRequest.get(`/bankaccounts/users/${userId}/anonymous`, { headers: linkTokenHeaders() })
    return response.data
  } catch (error) {
    const refusal = linkTokenRefusalOf(error)
    if (refusal) return rejectWithValue(refusal)
    const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to get anonymous bank account'
    return rejectWithValue(errorMessage)
  }
})

export const removeBankPayment = createAsyncThunk('payment/removeBankPayment', async ({
                                                                                        accountId,
                                                                                        userId
                                                                                      }, { rejectWithValue }) => {
  try {
    const response = await apiRequest.delete(`/bankaccounts/${accountId}/users/${userId}`)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to remove Bank method'
    return rejectWithValue(errorMessage)
  }
})
export const removeCardPayment = createAsyncThunk('payment/removeCardPayment', async ({
                                                                                        cardId,
                                                                                        userId
                                                                                      }, { rejectWithValue }) => {
  try {
    const response = await apiRequest.delete(`/cards/${cardId}/users/${userId}`)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to remove Card method'
    return rejectWithValue(errorMessage)
  }
})

// Send the status the operator picked, rather than letting the server invert whatever it
// finds. The API has accepted an explicit `status` for a while; nothing ever sent one, which
// was invisible while there were only two states — inverting and picking agree. With Canceled
// as a third it stops working, because there is no way to toggle *to* it.
//
// Still accepts a bare userId so the sidebar's own two-state switch keeps working.
export const togglePlanStatus = createAsyncThunk('plan/togglePlanStatus', async (arg, { rejectWithValue }) => {
  const userId = typeof arg === 'string' ? arg : arg?.userId
  const status = typeof arg === 'string' ? undefined : arg?.status
  try {
    const response = await apiRequest.put(`/plans/status/users/${userId}`, status ? { status } : undefined)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to toggle plan status'
    return rejectWithValue(errorMessage)
  }
})
